import '../../styles/workout-summary-system.css';
import { mdiLungs, mdiClockOutline, mdiMapMarkerDistance, mdiLightningBolt, mdiSpeedometer, mdiFire, mdiHeartPulse, mdiBike, mdiRunFast } from '@mdi/js';
import { workoutMetricLabel } from '../../utils/workoutMetricLabel';
import { workoutDisplayDigits as displayDigits } from '../../utils/workoutNumberFormat';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatZoneTime } from '../../utils/secToHMS';
import type { ComparisonRowItem, HeroMetricItem, HeroSubjectiveMetricItem, StatsRowItem } from './workoutSummaryHelpers';

const SUMMARY_METRIC_ICONS: Record<string, [string, string]> = {
  time: [mdiClockOutline, 'var(--text-info)'], distance: [mdiMapMarkerDistance, 'var(--text-success)'],
  tss: [mdiLightningBolt, 'var(--text-warning)'], if: [mdiSpeedometer, 'var(--ds-text-link)'],
  calories: [mdiFire, 'var(--text-warning)'], heart_rate: [mdiHeartPulse, 'var(--text-danger)'],
  respiration_rate: [mdiLungs, 'var(--text-info)'],
  power: [mdiLightningBolt, 'var(--text-warning)'], cadence: [mdiBike, 'var(--ds-text-link)'], speed: [mdiSpeedometer, 'var(--text-info)'],
};
function SummaryMetricIcon({ metric }: { metric: string }) {
  const [path, color] = SUMMARY_METRIC_ICONS[metric] ?? [mdiRunFast, 'var(--text-success)'];
  return <span className="ws-metric-icon" style={{ color }} aria-hidden="true"><svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" focusable="false"><path d={path} /></svg></span>;
}

const ZONE_GRADIENTS = [
  'linear-gradient(180deg, #3CC67D, #1FAE63)',
  'linear-gradient(180deg, #29C8D4, #1A9BA5)',
  'linear-gradient(180deg, #F5B740, #E0981A)',
  'linear-gradient(180deg, #FF8A65, #E55427)',
  'linear-gradient(180deg, #EA4782, #BE1553)',
  'linear-gradient(180deg, #9C27B0, #7B1FA2)',
  'linear-gradient(180deg, #5C6BC0, #3949AB)',
];

const ZONE_DOTS = ['#3CC67D', '#29C8D4', '#F5B740', '#FF8A65', '#EA4782', '#9C27B0', '#5C6BC0'];

const valOrDash = (v: string | number | null | undefined) =>
  v === null || v === undefined || v === '' ? '—' : displayDigits(v);

export function WsHeroCard({
  duration,
  metrics = [],
  subjectiveMetrics = [],
  onSubjectiveClick,
}: {
  eyebrow?: string;
  duration: string;
  metrics?: HeroMetricItem[];
  subjectiveMetrics?: HeroSubjectiveMetricItem[];
  onSubjectiveClick?: () => void;
  sportIcon?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const toneClass = (tone: HeroMetricItem['tone']) =>
    tone === 'green' ? 'ws-hero__metric-val--green' : tone === 'gold' ? 'ws-hero__metric-val--gold' : 'ws-hero__metric-val--pink';

  return (
    <div className="ws-hero">
      <div className="ws-hero__duration">
        <div className="ws-hero__duration-val ltr-island">{displayDigits(duration)}</div>
        <div className="ws-hero__duration-lbl">{t('workoutDetail.summary.totalDuration')}</div>
      </div>
      {metrics.length > 0 && (
        <div className="ws-hero__metrics">
          {metrics.map((m, i) => (
            <div key={`${m.label}-${i}`} className="ws-hero__metric">
              <span className={`ws-hero__metric-val ltr-island ${toneClass(m.tone)}`}>{typeof m.value === 'string' || typeof m.value === 'number' ? displayDigits(m.value) : m.value}</span>
              <span className="ws-hero__metric-lbl" style={{ textTransform: ['rTss', 'hrTSS'].includes(m.label) ? 'none' : undefined }}>{workoutMetricLabel(m.label)}</span>
            </div>
          ))}
        </div>
      )}
      {subjectiveMetrics.length > 0 && (
        onSubjectiveClick ? (
          <button
            type="button"
            className="ws-hero__subjective ws-hero__subjective--clickable"
            onClick={onSubjectiveClick}
            aria-label={t('workoutDetail.summary.heroSubjectiveEdit')}
          >
            {subjectiveMetrics.map((m) => (
              <div key={m.key} className="ws-hero__subjective-metric">
                <span
                  className={
                    m.isEmoji
                      ? 'ws-hero__subjective-val ws-hero__subjective-val--emoji'
                      : 'ws-hero__subjective-val ws-hero__subjective-val--rpe ltr-island'
                  }
                >
                  {typeof m.value === 'string' || typeof m.value === 'number' ? displayDigits(m.value) : m.value}
                </span>
                <span className="ws-hero__subjective-lbl">{workoutMetricLabel(m.label)}</span>
              </div>
            ))}
          </button>
        ) : (
          <div className="ws-hero__subjective">
            {subjectiveMetrics.map((m) => (
              <div key={m.key} className="ws-hero__subjective-metric">
                <span
                  className={
                    m.isEmoji
                      ? 'ws-hero__subjective-val ws-hero__subjective-val--emoji'
                      : 'ws-hero__subjective-val ws-hero__subjective-val--rpe ltr-island'
                  }
                >
                  {typeof m.value === 'string' || typeof m.value === 'number' ? displayDigits(m.value) : m.value}
                </span>
                <span className="ws-hero__subjective-lbl">{workoutMetricLabel(m.label)}</span>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

export function WsSectionCard({
  title,
  subtitle,
  children,
  onClick,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  onClick?: () => void;
}) {
  const inner = (
    <>
      <div className="ws-section__head">
        <span className="ws-section__title">{title}</span>
        {subtitle ? <span className="ws-section__sub">{subtitle}</span> : null}
      </div>
      {children}
    </>
  );

  if (onClick) {
    return (
      <button type="button" className="ws-section ws-section--clickable" onClick={onClick}>
        {inner}
      </button>
    );
  }
  return <div className="ws-section">{inner}</div>;
}

export function WsZoneStrip({
  title,
  zones,
  icon,
}: {
  title: string;
  zones: Array<{ time_in_seconds: number; percent_in_zone?: number; zone?: number }>;
  icon?: React.ReactNode;
}) {
  if (!zones.length) return null;

  const nonEmptyZones = zones.filter((z) => (z.time_in_seconds ?? 0) > 0 || (z.percent_in_zone ?? 0) > 0);
  const displayZones = nonEmptyZones.length ? nonEmptyZones : zones;
  const totalSeconds = displayZones.reduce((sum, z) => sum + (z.time_in_seconds ?? 0), 0);
  const weights = displayZones.map((z) => {
    if (typeof z.percent_in_zone === 'number' && z.percent_in_zone > 0) return z.percent_in_zone;
    if (totalSeconds > 0) return (z.time_in_seconds / totalSeconds) * 100;
    return 1;
  });

  return (
    <div className="ws-zone">
      <div className="ws-zone__head">
        <div className="ws-zone__title-row">
          {icon}
          <span className="ws-zone__title">{title}</span>
        </div>
        <span className="ws-zone__total ltr-island">{displayDigits(formatZoneTime(totalSeconds))}</span>
      </div>
      <div className="ws-zone__blocks">
        {displayZones.map((z, i) => {
          const zoneIndex = Math.max(0, (z.zone ?? i + 1) - 1);
          return (
            <div
              key={z.zone ?? i}
              className="ws-zone__block"
              style={{
                flex: Math.max(weights[i], 0.2),
                background: ZONE_GRADIENTS[zoneIndex] ?? ZONE_GRADIENTS[ZONE_GRADIENTS.length - 1],
              }}
              aria-label={`Z${z.zone ?? i + 1}: ${displayDigits(formatZoneTime(z.time_in_seconds))}`}
            />
          );
        })}
      </div>
      <div className="ws-zone__summary">
        {displayZones.map((z, i) => {
          const zoneIndex = Math.max(0, (z.zone ?? i + 1) - 1);
          return (
            <div key={z.zone ?? i} className="ws-zone__item">
              <span className="ws-zone__dot" style={{ background: ZONE_DOTS[zoneIndex] ?? ZONE_DOTS[ZONE_DOTS.length - 1] }} />
              <strong>Z{z.zone ?? i + 1}</strong>
              <span className="ltr-island">{displayDigits(formatZoneTime(z.time_in_seconds))}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function WsComparisonTable({ rows }: { rows: ComparisonRowItem[] }) {
  const { t } = useTranslation();
  if (!rows.length) return null;

  const labelFor = (key: ComparisonRowItem['rowKey']) => t(`workoutDetail.metrics.${key}`);

  return (
    <div className="ws-section">
      <div className="ws-section__head">
        <span className="ws-section__title">{t('workoutDetail.summary.comparisonTitle')}</span>
        <span className="ws-section__sub">{t('workoutDetail.summary.comparisonSub')}</span>
      </div>
      <div className="ws-table">
        <div className="ws-table__head ws-table__row ws-table__row--3">
          <span />
          <span>{t('workoutDetail.summary.planned')}</span>
          <span>{t('workoutDetail.summary.done')}</span>
        </div>
        {rows.map((r, i) => (
          <div key={r.rowKey} className={`ws-table__row ws-table__row--3${i < rows.length - 1 ? ' ws-table__row--border' : ''}`}>
            <span className="ws-table__label ws-table__label--icon"><SummaryMetricIcon metric={r.rowKey} /><span>{r.label ?? labelFor(r.rowKey)}</span></span>
            <span className="ws-table__cell ltr-island">{valOrDash(r.planned)}</span>
            <span
              className={`ws-table__cell ws-table__cell--done ltr-island${r.doneTone === 'red' ? ' ws-table__cell--red' : ''}${r.doneTone === 'green' ? ' ws-table__cell--green' : ''}`}
            >
              {valOrDash(r.done)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function WsStatsTable({ rows }: { rows: StatsRowItem[] }) {
  const { t } = useTranslation();
  if (!rows.length) return null;

  const captionFor = (row: StatsRowItem) =>
    row.rowKey === 'respiration_rate' ? t('runningMetrics.respiration_rate') : t(`workoutDetail.summary.stat.${row.rowKey}`, { defaultValue: row.caption ?? row.rowKey });

  return (
    <div className="ws-section">
      <div className="ws-section__head">
        <span className="ws-section__title">{t('workoutDetail.summary.statsTitle')}</span>
      </div>
      <div className="ws-table">
        <div className="ws-table__head ws-table__row ws-table__row--4">
          <span />
          <span>{t('workoutDetail.summary.min')}</span>
          <span>{t('workoutDetail.summary.avg')}</span>
          <span>{t('workoutDetail.summary.max')}</span>
        </div>
        {rows.map((r, i) => (
          <div key={r.rowKey} className={`ws-table__row ws-table__row--4${i < rows.length - 1 ? ' ws-table__row--border' : ''}`}>
            <span className="ws-table__label-col">
              <span className="ws-table__caption ws-table__label--icon"><SummaryMetricIcon metric={r.rowKey} /><span>{captionFor(r)}</span></span>
              <span className="ws-table__unit ltr-island">{r.rowKey === 'respiration_rate' ? t('runningMetrics.breathsPerMinute') : r.label}</span>
            </span>
            <span className="ws-table__cell ltr-island">{valOrDash(r.min)}</span>
            <span className="ws-table__cell ltr-island">{valOrDash(r.avg)}</span>
            <span className="ws-table__cell ltr-island">{valOrDash(r.max)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function WsCommentInvite({
  commentCount = 0,
  onPress,
}: {
  commentCount?: number;
  onPress?: () => void;
}) {
  const { t } = useTranslation();
  if (!onPress) return null;

  const hasComments = commentCount > 0;

  return (
    <button
      type="button"
      className={`ws-comment-invite${hasComments ? ' ws-comment-invite--active' : ''}`}
      onClick={onPress}
      aria-label={t('workoutDetail.summary.commentInviteCta')}
    >
      <span className="ws-comment-invite__icon" aria-hidden>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 11.5a8.5 8.5 0 0 1-12.4 7.5L3 21l2.1-5A8.5 8.5 0 1 1 21 11.5z" />
        </svg>
      </span>
      <span className="ws-comment-invite__body">
        <span className="ws-comment-invite__title">
          {hasComments
            ? t('workoutDetail.summary.commentInviteTitleActive')
            : t('workoutDetail.summary.commentInviteTitle')}
        </span>
        <span className="ws-comment-invite__sub">
          {hasComments
            ? t('workoutDetail.summary.commentInviteSubtitleActive', { count: commentCount })
            : t('workoutDetail.summary.commentInviteSubtitle')}
        </span>
      </span>
      {hasComments ? (
        <span className="ws-comment-invite__badge ltr-island">
          {commentCount}
        </span>
      ) : (
        <span className="ws-comment-invite__cta" aria-hidden>
          {t('workoutDetail.summary.commentInviteCta')}
        </span>
      )}
    </button>
  );
}

export function WsReactions({
  items,
}: {
  items: Array<{ key: string; label: string; icon: React.ReactNode; active?: boolean; onPress?: () => void }>;
}) {
  if (!items.length) return null;
  return (
    <div className="ws-reactions">
      {items.map((item) => (
        <button
          key={item.key}
          type="button"
          className={`ws-reactions__btn${item.active ? ' ws-reactions__btn--active' : ''}`}
          onClick={item.onPress}
        >
          {item.icon}
          <span>{item.label}</span>
        </button>
      ))}
    </div>
  );
}

export function WsNotesCard({
  label,
  value,
  onChange,
  placeholder,
  text,
  inset,
}: {
  label: string;
  value?: string;
  onChange?: (v: string) => void;
  placeholder?: string;
  text?: string;
  inset?: boolean;
}) {
  return (
    <div className={`ws-notes${inset ? ' ws-notes--inset' : ''}`}>
      <span className="ws-notes__label">{label}</span>
      {onChange ? (
        <textarea
          className="ws-notes__input"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={3}
        />
      ) : (
        <p className="ws-notes__text">{text || placeholder || '—'}</p>
      )}
    </div>
  );
}
